import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  ArrowLeft,
  PenLine,
  Type,
  Calendar,
  User,
  Mail,
  Building2,
  CheckSquare,
  Baseline,
  Send,
  X,
  FileText,
  ChevronDown,
  Check,
  ZoomIn,
  ZoomOut,
  Maximize2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { fileUrl, sendEnvelope } from "../../api";
import { recipientColor as recipientColorShared } from "../../lib/recipientColors";
import { companyName, companyAccent } from "../../companies";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FIELD_TYPES = [
  { key: "signature", label: "Signature", icon: PenLine, w: 150, h: 46 },
  { key: "initials", label: "Initials", icon: Baseline, w: 70, h: 46 },
  { key: "date", label: "Date Signed", icon: Calendar, w: 120, h: 36 },
  { key: "name", label: "Name", icon: User, w: 150, h: 36 },
  { key: "email", label: "Email", icon: Mail, w: 170, h: 36 },
  { key: "text", label: "Text", icon: Type, w: 140, h: 36 },
  { key: "checkbox", label: "Checkbox", icon: CheckSquare, w: 28, h: 28 },
  { key: "company", label: "Company", icon: Building2, w: 150, h: 36 },
];

const BASE_WIDTH = 820;
const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2;

const primaryCompanyKey = (r, activeKey) =>
  r?.company ||
  ((r?.companies || []).includes(activeKey) ? activeKey : (r?.companies || [])[0]);

const CompanyChip = ({ companyKey, cross, className = "" }) => {
  if (!companyKey) return null;
  const accent = companyAccent(companyKey);
  return (
    <span
      data-testid={`company-chip-${companyKey}`}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        cross ? "" : "bg-[#EFE9E2] text-[#8B8680] dark:bg-white/10 dark:text-white/55"
      } ${className}`}
      style={cross ? { background: `${accent}22`, color: accent } : undefined}
      title={cross ? `Different company: ${companyName(companyKey)}` : companyName(companyKey)}
    >
      <Building2 className="h-2.5 w-2.5" strokeWidth={2.4} />
      {companyName(companyKey)}
    </span>
  );
};

const AddFieldsEditor = ({ onAction, company }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;

  const [numPages, setNumPages] = useState(0);
  const [fields, setFields] = useState(() => {
    const incoming = location.state?.fields || [];
    const recs = location.state?.recipients || [];
    const fls = location.state?.files || [];
    const keyOf = (x) => x?.id || x?.storage_path || x?.name;
    return incoming
      .map((f) => {
        // Re-resolve a placed field to the CURRENT recipient/doc by identity, so
        // it survives a back-and-forward trip unless its recipient/doc was removed.
        let ri = typeof f.recipient === "number" ? f.recipient : 0;
        if (f.recipientEmail != null) {
          ri = recs.findIndex((r) => r.email === f.recipientEmail);
        }
        let di = typeof f.docIndex === "number" ? f.docIndex : 0;
        if (f.docKey != null) {
          di = fls.findIndex((x) => keyOf(x) === f.docKey);
        }
        if (ri < 0 || di < 0) return null;
        const { recipientEmail, docKey, ...rest } = f;
        return { ...rest, recipient: ri, docIndex: di };
      })
      .filter(Boolean);
  });
  const [activeRecipient, setActiveRecipient] = useState(0);
  const [activeDoc, setActiveDoc] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [sending, setSending] = useState(false);
  const pageRefs = useRef({});
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const zoomRef = useRef(1);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    if (!state || !state.files || state.files.length === 0) {
      navigate("/", { replace: true });
    }
  }, [state, navigate]);

  const onMouseMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const z = zoomRef.current;
    const rect = d.container.getBoundingClientRect();
    let x = e.clientX - rect.left - d.offsetX;
    let y = e.clientY - rect.top - d.offsetY;
    x = Math.max(0, Math.min(x, rect.width - d.width * z));
    y = Math.max(0, Math.min(y, rect.height - d.height * z));
    setFields((prev) =>
      prev.map((f) => (f.id === d.id ? { ...f, x: x / z, y: y / z } : f))
    );
  }, []);

  const onMouseUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  if (!state || !state.files) return null;

  const { files, recipients = [], subject, message = "", category = null, orderEnabled = false } = state;
  const currentFile = files[activeDoc];
  const isPdf = (currentFile?.type || "").toUpperCase() === "PDF";
  const totalPages = isPdf ? numPages : currentFile?.pages || 1;
  const pageWidth = Math.round(BASE_WIDTH * zoom);

  const trackPage = () => {
    const container = scrollRef.current;
    if (!container) return;
    const cRect = container.getBoundingClientRect();
    const center = cRect.top + cRect.height / 2;
    let best = 1;
    let bestDist = Infinity;
    for (let p = 1; p <= totalPages; p++) {
      const el = pageRefs.current[p];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const d = Math.abs(r.top + r.height / 2 - center);
      if (d < bestDist) {
        bestDist = d;
        best = p;
      }
    }
    setCurrentPage(best);
  };

  const addField = (type) => {
    const ft = FIELD_TYPES.find((f) => f.key === type);
    const page = currentPage || 1;
    const el = pageRefs.current[page];
    const container = scrollRef.current;
    const z = zoom;
    let yBase = 80;
    if (el && container) {
      const cRect = container.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      const visibleTop = Math.max(0, cRect.top - r.top);
      yBase = visibleTop / z + 60;
      const maxY = el.clientHeight / z - ft.h - 20;
      yBase = Math.min(yBase, Math.max(20, maxY));
    }
    const onPage = fields.filter(
      (f) => f.page === page && f.docIndex === activeDoc
    ).length;
    const stagger = (onPage % 6) * 22;
    setFields((prev) => [
      ...prev,
      {
        id: `${type}-${Date.now()}`,
        type,
        label: ft.label,
        page,
        docIndex: activeDoc,
        recipient: activeRecipient,
        x: BASE_WIDTH / 2 - ft.w / 2 + stagger,
        y: yBase + stagger,
        w: ft.w,
        h: ft.h,
      },
    ]);
  };

  const startDrag = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    const container = pageRefs.current[field.page];
    if (!container) return;
    const z = zoom;
    const rect = container.getBoundingClientRect();
    dragRef.current = {
      id: field.id,
      container,
      offsetX: e.clientX - rect.left - field.x * z,
      offsetY: e.clientY - rect.top - field.y * z,
      width: field.w,
      height: field.h,
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const removeField = (id) =>
    setFields((prev) => prev.filter((f) => f.id !== id));

  const handleSend = async () => {
    if (sending) return;
    if (!company?.key) {
      onAction("No active company selected");
      return;
    }
    // Only "Needs to Sign" recipients require a field. "Receives a Copy"
    // recipients are just cc'd the final document and never place a field.
    const missing = recipients.filter(
      (r, idx) =>
        (r.role || "Needs to Sign") === "Needs to Sign" &&
        !fields.some((f) => f.recipient === idx)
    );
    if (missing.length > 0) {
      const who = missing.map((r) => r.name || r.email).filter(Boolean).join(", ");
      onAction(
        `Add at least one field for ${who || "every recipient"} before sending.`
      );
      return;
    }
    setSending(true);
    try {
      await sendEnvelope({
        company: company.key,
        subject,
        message,
        category,
        base_width: BASE_WIDTH,
        signing_order: !!orderEnabled,
        files: files.map((f) => ({
          id: f.id,
          name: f.name,
          size: f.size,
          pages: f.pages,
          type: f.type,
          storage_path: f.storage_path,
        })),
        recipients: recipients.map((r) => ({
          id: r.id,
          name: r.name,
          email: r.email,
          role: r.role,
          avatar: r.avatar,
          company: r.company || null,
        })),
        fields: fields.map((f) => ({
          id: f.id,
          type: f.type,
          page: f.page,
          x: f.x,
          y: f.y,
          w: f.w,
          h: f.h,
          docIndex: f.docIndex,
          recipient_email: recipients[f.recipient]?.email || null,
        })),
      });
      onAction(`Envelope "${subject}" sent to ${recipients.length} recipient(s)`);
      navigate("/", { replace: true });
    } catch (e) {
      onAction(
        e?.response?.data?.detail || "Failed to send envelope. Please try again."
      );
      setSending(false);
    }
  };

  const recipientColor = (i) => recipientColorShared(i);
  const scrollToPage = (p) =>
    pageRefs.current[p]?.scrollIntoView({ behavior: "smooth", block: "start" });

  const changeZoom = (dir) =>
    setZoom((z) => {
      const next = dir === "in" ? z + 0.2 : z - 0.2;
      return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(next * 100) / 100));
    });

  const renderFieldsForPage = (pageNum) =>
    fields
      .filter((f) => f.page === pageNum && f.docIndex === activeDoc)
      .map((f) => {
        const color = recipientColor(f.recipient);
        const FT = FIELD_TYPES.find((t) => t.key === f.type);
        const Icon = FT.icon;
        return (
          <div
            key={f.id}
            data-testid={`placed-field-${f.type}`}
            onMouseDown={(e) => startDrag(e, f)}
            className="group absolute flex cursor-move items-center gap-1.5 rounded-md border-2 px-2 text-xs font-semibold shadow-sm"
            style={{
              left: f.x * zoom,
              top: f.y * zoom,
              width: f.w * zoom,
              height: f.h * zoom,
              borderColor: color,
              backgroundColor: `${color}22`,
              color,
            }}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            {f.type !== "checkbox" && <span className="truncate">{f.label}</span>}
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeField(f.id);
              }}
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#E5484D] opacity-0 shadow transition-opacity group-hover:opacity-100 dark:bg-[#1A1721]"
            >
              <X className="h-3 w-3" strokeWidth={2.5} />
            </button>
          </div>
        );
      });

  const backToSend = () => {
    const keyOf = (x) => x?.id || x?.storage_path || x?.name;
    const fieldsOut = fields.map((f) => ({
      ...f,
      recipientEmail: recipients[f.recipient]?.email || null,
      docKey: keyOf(files[f.docIndex]) || null,
    }));
    navigate("/send", {
      state: { files, recipients, subject, message, category, orderEnabled, fields: fieldsOut },
    });
  };

  return (
    <div className="px-4 pt-6 md:px-8 md:pt-8">
      {/* Top bar */}
      <header className="glass-panel mb-6 flex flex-wrap items-center justify-between gap-4 rounded-[22px] px-5 py-4">
        <div className="flex items-center gap-4">
          <button
            data-testid="editor-back-btn"
            onClick={backToSend}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:border dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
          </button>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-[#1A1815] dark:text-white">
              Add Fields
            </h1>
            <p className="truncate text-sm text-[#8B8680] dark:text-white/45">
              {subject}
            </p>
          </div>
        </div>
        <button
          data-testid="editor-send-btn"
          onClick={handleSend}
          disabled={sending}
          className="flex items-center gap-2 rounded-xl bg-[#1A1815] px-7 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03] active:scale-95 disabled:opacity-60 disabled:hover:scale-100 dark:bg-white dark:text-black"
        >
          {sending ? "Sending…" : "Send"}
          <Send className="h-4 w-4" strokeWidth={1.9} />
        </button>
      </header>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[230px_1fr_180px]">
        {/* Left: field palette */}
        <aside className="glass-panel h-fit rounded-[22px] p-5">
          <div className="mb-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8B8680] dark:text-white/40">
              Assign to recipient
            </p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="recipient-select-btn"
                  className="flex w-full items-center justify-between gap-2 rounded-xl border border-[#E7E0D8] bg-[#F6EFE8] px-3 py-2.5 text-sm font-medium text-[#1A1815] dark:border-white/10 dark:bg-white/5 dark:text-white"
                >
                  <span className="flex items-center gap-2 truncate">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ background: recipientColor(activeRecipient) }}
                    />
                    <span className="truncate">
                      {recipients[activeRecipient]?.name || "Select recipient"}
                    </span>
                    {recipients[activeRecipient]?.companies && (
                      <CompanyChip
                        companyKey={primaryCompanyKey(recipients[activeRecipient], company?.key)}
                        cross={primaryCompanyKey(recipients[activeRecipient], company?.key) !== company?.key}
                      />
                    )}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0" strokeWidth={2.2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                style={{ fontFamily: "Urbanist, sans-serif" }}
                className="w-64 rounded-2xl border-[#E7E0D8] bg-white p-1.5 shadow-lg dark:border-white/10 dark:bg-[#141019]"
              >
                {recipients.map((r, i) => (
                  <DropdownMenuItem
                    key={r.id || r.email || i}
                    data-testid={`recipient-option-${i}`}
                    onClick={() => setActiveRecipient(i)}
                    className="flex cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-medium text-[#1A1815] focus:bg-[#F0EBE5] dark:text-white dark:focus:bg-white/10"
                  >
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                      style={{ background: recipientColor(i) }}
                    >
                      {(r.name || "?").charAt(0)}
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{r.name}</span>
                      {r.email && (
                        <span className="truncate text-[11px] text-[#8B8680] dark:text-white/40">
                          {r.email}
                        </span>
                      )}
                    </span>
                    {r.companies && (
                      <CompanyChip
                        companyKey={primaryCompanyKey(r, company?.key)}
                        cross={primaryCompanyKey(r, company?.key) !== company?.key}
                        className="ml-auto"
                      />
                    )}
                    {i === activeRecipient && (
                      <Check className="ml-2 h-4 w-4 shrink-0" strokeWidth={2.4} />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8B8680] dark:text-white/40">
            Fields
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {FIELD_TYPES.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                data-testid={`field-btn-${key}`}
                onClick={() => addField(key)}
                className="flex flex-col items-center gap-1.5 rounded-2xl border border-[#E7E0D8] bg-[#F6EFE8] px-2 py-3 text-xs font-semibold text-[#1A1815] transition-all hover:-translate-y-0.5 hover:border-[#1A1815] dark:border-white/10 dark:bg-white/5 dark:text-white/80 dark:hover:border-white/40"
              >
                <Icon className="h-5 w-5" strokeWidth={1.7} />
                {label}
              </button>
            ))}
          </div>
          <p className="mt-4 text-[11px] leading-snug text-[#B0A99F] dark:text-white/35">
            Fields drop onto the page you're viewing (page {currentPage}). Drag to fine-tune.
          </p>
        </aside>

        {/* Center: document preview */}
        <main className="min-w-0">
          {/* Toolbar */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {files.length > 1 &&
                files.map((f, i) => (
                  <button
                    key={i}
                    data-testid={`doc-tab-${i}`}
                    onClick={() => setActiveDoc(i)}
                    className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition-colors ${
                      i === activeDoc
                        ? "bg-[#1A1815] text-white dark:bg-white dark:text-black"
                        : "border border-[#E7E0D8] bg-[#F6EFE8] text-[#1A1815] dark:border-white/10 dark:bg-white/5 dark:text-white/70"
                    }`}
                  >
                    <FileText className="h-3.5 w-3.5" strokeWidth={1.8} />
                    <span className="max-w-[140px] truncate">{f.name}</span>
                  </button>
                ))}
            </div>
            <div className="flex items-center gap-1 rounded-full border border-[#E7E0D8] bg-[#F6EFE8] p-1 dark:border-white/10 dark:bg-white/5">
              <button
                data-testid="zoom-out-btn"
                onClick={() => changeZoom("out")}
                disabled={zoom <= MIN_ZOOM}
                className="flex h-8 w-8 items-center justify-center rounded-full text-[#1A1815] transition-colors hover:bg-white disabled:opacity-40 dark:text-white/70 dark:hover:bg-white/10"
              >
                <ZoomOut className="h-4 w-4" strokeWidth={1.9} />
              </button>
              <span
                data-testid="zoom-level"
                className="min-w-[48px] text-center text-xs font-bold text-[#1A1815] dark:text-white"
              >
                {Math.round(zoom * 100)}%
              </span>
              <button
                data-testid="zoom-in-btn"
                onClick={() => changeZoom("in")}
                disabled={zoom >= MAX_ZOOM}
                className="flex h-8 w-8 items-center justify-center rounded-full text-[#1A1815] transition-colors hover:bg-white disabled:opacity-40 dark:text-white/70 dark:hover:bg-white/10"
              >
                <ZoomIn className="h-4 w-4" strokeWidth={1.9} />
              </button>
              <button
                data-testid="zoom-reset-btn"
                onClick={() => setZoom(1)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-[#1A1815] transition-colors hover:bg-white dark:text-white/70 dark:hover:bg-white/10"
              >
                <Maximize2 className="h-3.5 w-3.5" strokeWidth={1.9} />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            onScroll={trackPage}
            className="glass-panel thin-scroll max-h-[calc(100vh-150px)] overflow-auto rounded-[22px] p-4 md:p-6"
          >
            <div className="flex flex-col items-center gap-6">
              {isPdf ? (
                <Document
                  file={fileUrl(currentFile.storage_path)}
                  onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                  onLoadError={() => onAction("Could not render PDF preview")}
                  loading={
                    <p className="py-10 text-sm text-[#8B8680] dark:text-white/45">
                      Loading document…
                    </p>
                  }
                >
                  {Array.from({ length: totalPages }, (_, idx) => {
                    const pageNum = idx + 1;
                    return (
                      <div
                        key={pageNum}
                        ref={(el) => (pageRefs.current[pageNum] = el)}
                        className="relative mb-6 overflow-hidden rounded-lg shadow-lg last:mb-0"
                        style={{ width: pageWidth }}
                      >
                        <Page
                          pageNumber={pageNum}
                          width={pageWidth}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                        {renderFieldsForPage(pageNum)}
                      </div>
                    );
                  })}
                </Document>
              ) : (
                Array.from({ length: totalPages }, (_, idx) => {
                  const pageNum = idx + 1;
                  return (
                    <div
                      key={pageNum}
                      ref={(el) => (pageRefs.current[pageNum] = el)}
                      className="relative flex flex-col items-center justify-start rounded-lg bg-white p-10 shadow-lg dark:bg-white/95"
                      style={{ width: pageWidth, height: pageWidth * 1.3 }}
                    >
                      <FileText className="mt-16 h-12 w-12 text-[#C9C2BA]" strokeWidth={1.4} />
                      <p className="mt-4 text-sm font-semibold text-[#8B8680]">
                        {currentFile.name}
                      </p>
                      <p className="text-xs text-[#B0A99F]">
                        Page {pageNum} of {totalPages} · preview unavailable
                      </p>
                      {renderFieldsForPage(pageNum)}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </main>

        {/* Right: page thumbnails */}
        <aside className="glass-panel thin-scroll h-fit max-h-[calc(100vh-150px)] overflow-y-auto rounded-[22px] p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#8B8680] dark:text-white/40">
            Pages ({totalPages})
          </p>
          <div className="space-y-3">
            {Array.from({ length: totalPages }, (_, idx) => {
              const pageNum = idx + 1;
              const count = fields.filter(
                (f) => f.page === pageNum && f.docIndex === activeDoc
              ).length;
              const isCurrent = pageNum === currentPage;
              return (
                <button
                  key={pageNum}
                  data-testid={`page-thumb-${pageNum}`}
                  onClick={() => scrollToPage(pageNum)}
                  className="group relative flex w-full flex-col items-center gap-1.5"
                >
                  <div
                    className={`relative w-full overflow-hidden rounded-lg border-2 bg-white shadow transition-colors dark:bg-white/90 ${
                      isCurrent
                        ? "border-[#1A1815] dark:border-white/70"
                        : "border-transparent group-hover:border-[#1A1815]/40 dark:group-hover:border-white/40"
                    }`}
                  >
                    {isPdf ? (
                      <Document
                        file={fileUrl(currentFile.storage_path)}
                        loading={<div className="aspect-[1/1.3] w-full" />}
                      >
                        <Page
                          pageNumber={pageNum}
                          width={150}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                      </Document>
                    ) : (
                      <div className="flex aspect-[1/1.3] w-full items-center justify-center">
                        <FileText className="h-6 w-6 text-[#C9C2BA]" strokeWidth={1.5} />
                      </div>
                    )}
                    {count > 0 && (
                      <span className="absolute right-1.5 top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#1A1815] px-1.5 text-[10px] font-bold text-white dark:bg-white dark:text-black">
                        {count}
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-xs font-medium ${
                      isCurrent
                        ? "text-[#1A1815] dark:text-white"
                        : "text-[#8B8680] dark:text-white/45"
                    }`}
                  >
                    {pageNum}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default AddFieldsEditor;
